import type React from 'react';
import { useEffect, useRef, useState } from 'react';

import { CopyButton, Tooltip } from '@vlabs/uikit';
import cn from 'classnames';

import st from './CopyableText.module.sass';
import { useCopyableTextTranslation } from './i18n';

interface CopyableTextProps {
  /** Текст для отображения и копирования в буфер. */
  value: string;
  className?: string;
  'data-testid'?: string;
}

function CopyableText({
  value,
  className = undefined,
  'data-testid': dataTestId = undefined,
}: CopyableTextProps): React.JSX.Element {
  const { t } = useCopyableTextTranslation();
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const el = textRef.current;
    if (!el) return undefined;

    const measure = () => setIsTruncated(el.scrollWidth > el.clientWidth);
    const observer = new ResizeObserver(measure);

    measure();
    observer.observe(el);

    return () => observer.disconnect();
  }, [value]);

  return (
    <div
      className={cn(st.Root, className)}
      data-testid={dataTestId}
    >
      <Tooltip
        className={st.TooltipWrap}
        tooltipContent={value}
        tooltipProps={{ hidden: !isTruncated }}
      >
        <span
          className={st.Text}
          ref={textRef}
        >
          {value}
        </span>
      </Tooltip>

      <Tooltip
        tooltipContent={t('copy')}
        tooltipProps={{ place: 'bottom' }}
      >
        <CopyButton
          className={st.CopyButton}
          size="small"
          value={value}
        />
      </Tooltip>
    </div>
  );
}

export { CopyableText };
